import { DirectionsBike, DirectionsRun, MonitorHeart, Sync, TrendingUp } from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import type { TrainingPlan, UserProfile, Workout } from '@shared/types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useWorkoutTypes } from '../../contexts/WorkoutTypesContext';
import { useTrainingPlanPersistence } from '../../hooks/useTrainingPlanPersistence';
import apiClient from '../../services/api';
import {
  createTrainingPlanManager,
  type TrainingPlanGenerationResult,
} from '../../services/trainingPlanManager';
import { updateCachedPlanMetadata } from '../../services/trainingPlanStorage';
import { getCachedWeather, type WeatherData } from '../../services/weatherService';
import {
  getClothingRecommendation,
  type ClothingRecommendation,
} from '../../utils/clothingRecommendation';
import { logger } from '../../utils/logger';
import { findWorkoutByDate, getAllWorkouts, getTotalPlannedDistance } from '../../utils/planUtils';
import { loadUserProfile, loadUserProfileSync } from '../../utils/userProfile';
import { TrainingActions } from '../calendar/TrainingActions';
import { TrainingCalendar } from '../calendar/TrainingCalendar';
import WorkoutModal from '../workout/WorkoutModal';
import AtlCtlModal from './AtlCtlModal';
import BottomToolbar from './BottomToolbar';
import HealthStatusWidget from './HealthStatusWidget';
import NoPlanDialog from './NoPlanDialog';
import WorkoutAvatarWidget from './WorkoutAvatarWidget';

const Dashboard: React.FC = () => {
  const { state: authState, checkAuth, refreshHealthData } = useAuth();
  const { showSuccess, showApiError, showInfo } = useNotification();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [trainingPlan, setTrainingPlan] = useState<TrainingPlan | null>(null);
  const [selectedWorkout, setSelectedWorkout] = useState<Workout | null>(null);
  const [isWorkoutModalOpen, setIsWorkoutModalOpen] = useState(false);
  const [isGeneratingPlan, setIsGeneratingPlan] = useState(false);
  const [showNoPlanDialog, setShowNoPlanDialog] = useState(false);
  // Shared weather/clothing state for both WorkoutAvatarWidget instances
  const [sharedWeather, setSharedWeather] = useState<WeatherData | null>(null);
  const [sharedClothing, setSharedClothing] = useState<ClothingRecommendation | null>(null);
  const [isLoadingWeather, setIsLoadingWeather] = useState(true);
  const [isDeletingWorkout, setIsDeletingWorkout] = useState(false);
  const [planRegenerationTrigger, setPlanRegenerationTrigger] = useState(0);
  const [isAtlCtlModalOpen, setIsAtlCtlModalOpen] = useState(false);
  const [isSyncingFitness, setIsSyncingFitness] = useState(false);
  const [trainingActionState, setTrainingActionState] = useState<{
    isSwapMode: boolean;
    isDeloadWeek: boolean;
    onSwapModeToggle: () => void;
    onDeloadWeekClick: () => void;
  } | null>(null);
  const [healthStatus, setHealthStatus] = useState<'normal' | 'sick' | 'injured'>('normal');
  const [healthStatusHistory, setHealthStatusHistory] = useState<
    Array<{
      status: 'normal' | 'sick' | 'injured';
      startDate: string;
      endDate: string | null;
      durationDays?: number;
    }>
  >([]);

  // Get workout types from context (shared across components, loaded once)
  const { workoutTypes } = useWorkoutTypes();

  // Training plan persistence
  const persistence = useTrainingPlanPersistence();
  const planManager = useMemo(() => createTrainingPlanManager(persistence), [persistence]);

  // Ref for fitness sync guard (prevents infinite loop with state-based dependency)
  const isSyncingFitnessRef = useRef(false);

  /**
   * Sync fitness data from Intervals.icu
   * @param force - Force sync even if data is fresh
   * @param silent - Don't show notifications (for auto-sync)
   */
  const syncFitnessData = useCallback(
    async (force: boolean = false, silent: boolean = false): Promise<boolean> => {
      // Use ref for guard to avoid recreating callback on state change
      if (isSyncingFitnessRef.current) {
        logger.debug('Fitness sync already in progress, skipping');
        return false;
      }

      try {
        isSyncingFitnessRef.current = true;
        setIsSyncingFitness(true);
        logger.info('Syncing fitness data from Intervals.icu', { force, silent });

        const response = await apiClient.syncFitnessData(force);

        if (response.success && response.data) {
          if (!response.data.connected) {
            // User not connected to Intervals.icu
            if (!silent) {
              showInfo('Connect to Intervals.icu in your profile settings to sync fitness data');
            }
            return false;
          }

          if (response.data.fitnessData) {
            // Refresh health data in auth context
            await refreshHealthData();

            if (!silent) {
              const msg = response.data.syncedFromCache
                ? 'Fitness data loaded from cache'
                : 'Fitness data synced from Intervals.icu';
              showSuccess(msg);
            }
            logger.info('Fitness data synced successfully', {
              atl: response.data.fitnessData.atl,
              ctl: response.data.fitnessData.ctl,
              fromCache: response.data.syncedFromCache,
            });
            return true;
          }
        }

        if (!silent) {
          showApiError(response, 'Failed to sync fitness data');
        }
        return false;
      } catch (error) {
        logger.error('Error syncing fitness data', error as Error);
        if (!silent) {
          showApiError(
            error instanceof Error ? error.message : 'Unknown error',
            'Failed to sync fitness data'
          );
        }
        return false;
      } finally {
        isSyncingFitnessRef.current = false;
        setIsSyncingFitness(false);
      }
    },
    [refreshHealthData, showSuccess, showInfo, showApiError]
  );

  // Check for success parameter from OAuth callback
  useEffect(() => {
    const success = searchParams.get('success');
    if (success === 'true') {
      showSuccess('Successfully connected to Intervals.icu!');
      // Trigger auth check to pick up the new token
      checkAuth();
      // Clean up the URL by removing the success parameter
      searchParams.delete('success');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, checkAuth, showSuccess]);

  // Check for stale fitness data when tab becomes visible again
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Sync fitness data when user returns to tab (will use cache if fresh)
        syncFitnessData(false, true); // Don't force, silent
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // syncFitnessData is stable via ref guard, no need to re-register listener

  // Load weather data once for both WorkoutAvatarWidget instances
  useEffect(() => {
    const loadSharedWeather = async () => {
      try {
        setIsLoadingWeather(true);
        const weatherData = await getCachedWeather();
        setSharedWeather(weatherData);

        if (weatherData) {
          const clothingRec = await getClothingRecommendation(weatherData);
          setSharedClothing(clothingRec);
        }
      } catch (err) {
        logger.error('Error loading shared weather', err as Error);
      } finally {
        setIsLoadingWeather(false);
      }
    };

    loadSharedWeather();
  }, []);

  // Load user profile on component mount - sync from backend
  useEffect(() => {
    const syncProfile = async () => {
      try {
        logger.info('Syncing user profile from backend on app load');
        const syncedProfile = await loadUserProfile(); // This fetches from backend and updates localStorage
        logger.info('User profile synced from backend', {
          thresholdPaceSec: syncedProfile.thresholdPaceSec,
          weeklyMileage: syncedProfile.weeklyMileage,
        });

        // Load health status
        const healthResponse = await apiClient.getHealthStatus();
        if (healthResponse.success && healthResponse.data) {
          setHealthStatus(healthResponse.data.healthStatus);
          setHealthStatusHistory(healthResponse.data.statusHistory || []);
        }

        // Sync fitness data from Intervals.icu on login (silent, no force)
        // This will use cached data if fresh, or fetch from Intervals.icu if stale
        syncFitnessData(false, true);

        // NOTE: Profile sync does NOT trigger plan regeneration
        // This just loads the stored plan or generates initial plan if none exists
        // Users must explicitly click \"Recalculate\" to regenerate based on profile changes
        setPlanRegenerationTrigger(prev => prev + 1);
      } catch (error) {
        logger.warn('Could not sync profile from backend, using cached version', error as Error);
      }
    };

    syncProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount - syncFitnessData is stable via ref guard

  // Workout types are loaded by WorkoutTypesContext (shared across components)

  useEffect(() => {
    // Generate training plan when profile is loaded
    // Health data from Intervals.icu is optional - will use defaults if not available
    // NOTE: This only generates the INITIAL plan or loads an existing one from storage
    // It does NOT automatically regenerate plans when profile changes
    // Users must explicitly click \"Recalculate\" button in profile settings to regenerate
    if (!persistence.isLoading) {
      generateInitialTrainingPlan();
    }
  }, [planRegenerationTrigger, persistence.isLoading]);

  const createUserProfile = (): UserProfile => {
    // Load saved user profile or use defaults
    const savedProfile = loadUserProfileSync();

    return {
      // Required fields
      userId: savedProfile?.userId || 'default_user',
      intervalsConnected: savedProfile?.intervalsConnected || false,
      syncToIntervals: savedProfile?.syncToIntervals,
      createdAt: savedProfile?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      // Use saved preferences or defaults
      thresholdPaceSec: savedProfile?.thresholdPaceSec || 240, // Default 4:00/km
      weeklyMileage: savedProfile?.weeklyMileage || authState.healthData?.weekly_target_km || 50,
      weeklyWorkoutPreferences: savedProfile?.weeklyWorkoutPreferences || {
        Monday: 'base run',
        Tuesday: 'hard run',
        Wednesday: 'base run',
        Thursday: 'base run',
        Friday: 'rest',
        Saturday: 'long run',
        Sunday: 'rest',
      },
      runnerCategory: savedProfile?.runnerCategory,
      fatigueLevel: savedProfile?.fatigueLevel || 'normal',
      // Always use fresh health data from intervals.icu
      ATL: authState.healthData?.atl || 50,
      CTL: authState.healthData?.ctl || 45,
      HRV: authState.healthData?.hrv_score || 50,
      // Include advanced settings if they exist in saved profile
      trainingConstants: savedProfile?.trainingConstants,
      fatigueThresholds: savedProfile?.fatigueThresholds,
      // Cycling fields
      FTP: savedProfile?.FTP,
    };
  };

  const generateInitialTrainingPlan = async () => {
    try {
      setIsGeneratingPlan(true);

      const userProfile = createUserProfile();

      // Wait for persistence to finish loading
      if (persistence.isLoading) {
        logger.debug('Persistence is still loading, waiting');
        return;
      }

      // Update the cached plan metadata with current user profile hash
      // This ensures compatibility check passes for plans loaded from backend
      // (e.g., after AI Coach modifications)
      if (persistence.hasStoredPlan) {
        updateCachedPlanMetadata(userProfile);
      }

      // Debug storage state
      const storageInfo = planManager.getStorageInfo();
      logger.debug('Storage debug info', {
        hasStoredPlan: persistence.hasStoredPlan,
        storedPlan: !!persistence.storedPlan,
        isLoading: persistence.isLoading,
        storageInfo,
        userProfile: {
          thresholdPaceSec: userProfile.thresholdPaceSec,
          weeklyMileage: userProfile.weeklyMileage,
          fatigueLevel: userProfile.fatigueLevel,
        },
      });

      // First try to load from storage
      const storedResult = await planManager.loadTrainingPlan(userProfile);

      if (storedResult) {
        logger.info('Loaded training plan from storage');
        setTrainingPlan(storedResult.plan);

        // NOTE: Do NOT sync stored plan to intervals.icu
        // The plan was already synced when it was generated/modified
        // Syncing on every page load causes duplicate events
        logger.debug('Skipping Intervals sync for stored plan - already synced previously', {
          source: storedResult.source,
        });

        showSuccess('Training plan loaded from storage!');

        // Log persistence info for debugging
        logger.debug('Training plan loaded from storage', {
          source: storedResult.source,
          wasStored: storedResult.wasStored,
          storageInfo: planManager.getStorageInfo(),
        });

        return;
      }

      // No stored plan available - show dialog to let user choose what to do
      logger.info('No stored plan found, showing options dialog');
      setShowNoPlanDialog(true);
    } catch (error) {
      logger.error('Training plan loading error', error as Error);
      // On error, also show the dialog so user can try again
      setShowNoPlanDialog(true);
    } finally {
      setIsGeneratingPlan(false);
    }
  };

  // Generate plan when user chooses to do so from the NoPlanDialog
  const handleGeneratePlanFromDialog = async () => {
    try {
      setIsGeneratingPlan(true);
      setShowNoPlanDialog(false);

      const userProfile = createUserProfile();

      logger.info('User chose to generate new plan from dialog');
      const result: TrainingPlanGenerationResult =
        await planManager.generateOrUpdatePlan(userProfile);

      setTrainingPlan(result.plan);

      // Intervals.icu sync is now handled automatically by the backend
      // when the plan is saved via updateStoredTrainingPlan

      // Show appropriate success message based on operation type
      const message =
        result.source === 'backend-updated'
          ? 'Training plan updated successfully!'
          : 'Training plan generated successfully!';

      showSuccess(message);

      // Log persistence info for debugging
      logger.debug('Training plan generation result', {
        source: result.source,
        wasStored: result.wasStored,
        storageInfo: planManager.getStorageInfo(),
      });
    } catch (error) {
      logger.error('Training plan generation error', error as Error);
      showApiError(
        error instanceof Error ? error.message : 'Unknown error',
        'Failed to generate training plan'
      );
    } finally {
      setIsGeneratingPlan(false);
    }
  };

  // Navigate to onboarding wizard
  const handleRunOnboarding = () => {
    logger.info('User chose to run onboarding wizard');
    setShowNoPlanDialog(false);
    // Clear onboarding completion flag to force the wizard to show
    localStorage.removeItem('pacepilot_onboarding_complete');
    navigate('/onboarding');
  };

  // Close the no plan dialog without action
  const handleCloseNoPlanDialog = () => {
    setShowNoPlanDialog(false);
    showInfo('You can generate a training plan later from the profile menu.');
  };

  // Listen for training plan updated events (e.g., from recalculate button or AI Coach)
  useEffect(() => {
    const handleTrainingPlanUpdated = async (event: Event) => {
      const customEvent = event as CustomEvent;
      const updatedPlan = customEvent.detail;
      if (updatedPlan) {
        logger.info('Training plan updated event received, updating UI', {
          workouts: getAllWorkouts(updatedPlan).length,
          totalKm: getTotalPlannedDistance(updatedPlan),
        });

        // Update local state FIRST before any async operations
        // Deep clone to ensure React detects the change and re-renders
        const planCopy = JSON.parse(JSON.stringify(updatedPlan));
        setTrainingPlan(planCopy);
        logger.info('Training plan state updated via setTrainingPlan', {
          planWorkouts: getAllWorkouts(planCopy).length,
        });

        // Sync fitness data from Intervals.icu when plan is regenerated
        syncFitnessData(true, true); // Force sync, silent

        // Update persistence (localStorage) to keep everything in sync
        try {
          const userProfile = createUserProfile();
          if (userProfile) {
            await persistence.saveToStorage(updatedPlan, userProfile);
            logger.info('Updated persistence layer with new plan');

            // Intervals.icu sync is now handled automatically by the backend
            // when the plan is saved via updateStoredTrainingPlan
          }
        } catch (error) {
          logger.error('Error updating persistence or syncing', error);
          // State was already updated, so UI should reflect changes
        }

        showSuccess('Training plan updated successfully!');
      }
    };

    window.addEventListener('training-plan-updated', handleTrainingPlanUpdated);
    return () => window.removeEventListener('training-plan-updated', handleTrainingPlanUpdated);
  }, [showSuccess, persistence, syncFitnessData]);

  // Listen for manual sync from backend
  useEffect(() => {
    const handleDataSynced = async () => {
      logger.info('Data synced from backend, loading fresh data');

      // Load the synced training plan from localStorage (which was updated by sync)
      const syncedPlan = await persistence.loadFromStorage();
      if (syncedPlan) {
        logger.info('Loaded synced training plan from storage');
        setTrainingPlan(syncedPlan);
      } else {
        logger.info('No training plan in storage after sync');
      }
    };

    window.addEventListener('data-synced-from-backend', handleDataSynced);
    return () => window.removeEventListener('data-synced-from-backend', handleDataSynced);
  }, [persistence]);

  // Listen for Intervals.icu auto-sync results from backend
  useEffect(() => {
    const handleIntervalsSyncCompleted = (event: Event) => {
      const customEvent = event as CustomEvent<{
        synced: boolean;
        successfulSyncs?: number;
        failedSyncs?: number;
        message?: string;
      }>;
      const syncResult = customEvent.detail;

      if (syncResult?.synced && syncResult.successfulSyncs) {
        showInfo(
          `Synced to Intervals.icu: ${syncResult.successfulSyncs} workout${syncResult.successfulSyncs !== 1 ? 's' : ''} synced successfully.`
        );
      } else if (syncResult?.synced === false && syncResult.message) {
        // Sync failed or was skipped - only show if there's an actionable message
        if (syncResult.message.includes('failed')) {
          showInfo(syncResult.message);
        }
      }
    };

    window.addEventListener('intervals-sync-completed', handleIntervalsSyncCompleted);
    return () =>
      window.removeEventListener('intervals-sync-completed', handleIntervalsSyncCompleted);
  }, [showInfo]);

  // Listen for background sync training plan updates
  useEffect(() => {
    const handleBackgroundTrainingPlanUpdate = (event: CustomEvent) => {
      const { trainingPlan } = event.detail;
      if (trainingPlan) {
        logger.info('Training plan updated from background sync');
        setTrainingPlan(trainingPlan);
        showSuccess('Training plan updated automatically based on latest health data!');
      }
    };

    window.addEventListener(
      'trainingPlanUpdatedFromSync',
      handleBackgroundTrainingPlanUpdate as EventListener
    );
    return () =>
      window.removeEventListener(
        'trainingPlanUpdatedFromSync',
        handleBackgroundTrainingPlanUpdate as EventListener
      );
  }, [showSuccess]);

  const handleWorkoutClick = (workout: Workout) => {
    setSelectedWorkout(workout);
    setIsWorkoutModalOpen(true);
  };

  const handleTrainingPlanUpdate = async (updatedPlan: TrainingPlan) => {
    setTrainingPlan(updatedPlan);

    // Update stored plan when plan is modified (syncs to backend)
    const userProfile = createUserProfile();
    if (userProfile) {
      await persistence.updateInStorage(updatedPlan, userProfile);
      logger.info('Updated stored training plan after modification (backend synced)');

      // Intervals.icu sync is now handled automatically by the backend
    }
  };

  const handleWorkoutRemove = async (workout: Workout) => {
    if (!trainingPlan || workout.index === undefined) return;

    const userProfile = createUserProfile();
    if (!userProfile) {
      showApiError(
        'Unable to create user profile for workout deletion',
        'Failed to delete workout'
      );
      return;
    }

    try {
      setIsDeletingWorkout(true);

      const response = await apiClient.deleteWorkout(userProfile, trainingPlan, workout.index);

      if (response.success && response.data) {
        setTrainingPlan(response.data);

        // Update stored plan (syncs to backend, which auto-syncs to Intervals.icu)
        await persistence.updateInStorage(response.data, userProfile);

        showSuccess(`Workout "${workout.name}" deleted successfully`);
        logger.info(`Workout deleted successfully`, { name: workout.name, index: workout.index });
      } else {
        showApiError(response, 'Failed to delete workout');
      }
    } catch (error) {
      logger.error('Workout deletion error', error as Error);
      showApiError(
        error instanceof Error ? error.message : 'Unknown error',
        'Failed to delete workout'
      );
    } finally {
      setIsDeletingWorkout(false);
    }
  };

  const handleCloseWorkoutModal = () => {
    setIsWorkoutModalOpen(false);
    setSelectedWorkout(null);
  };

  const handleLongRunDistanceUpdate = async (workoutIndex: number, newDistance: number) => {
    const userProfile = createUserProfile();
    if (!trainingPlan || !userProfile) {
      showApiError('No training plan or user profile available', 'Cannot update distance');
      return;
    }

    try {
      logger.info('Updating long run distance', { workoutIndex, newDistance });
      const response = await apiClient.updateLongRunDistance(
        userProfile,
        trainingPlan,
        workoutIndex,
        newDistance
      );

      if (response.success && response.data) {
        // Update UI directly - backend already persisted the changes
        setTrainingPlan(response.data);

        // Update the selected workout in the modal with the updated data
        if (selectedWorkout && selectedWorkout.index === workoutIndex) {
          const result = findWorkoutByDate(response.data, selectedWorkout.training_date);
          if (result?.workout) {
            setSelectedWorkout(result.workout);
          }
        }

        // Update localStorage cache (no backend call - already saved by endpoint)
        localStorage.setItem('trainingPlan', JSON.stringify(response.data));

        showSuccess(`Long run distance updated to ${newDistance}km`);
        logger.info('Long run distance updated successfully');
      } else {
        showApiError(response, 'Failed to update long run distance');
      }
    } catch (error) {
      logger.error('Long run distance update error', error as Error);
      showApiError(
        error instanceof Error ? error.message : 'Unknown error',
        'Failed to update long run distance'
      );
    }
  };

  const handleWorkoutTypeChange = async (workoutIndex: number, newWorkoutTypeId: string) => {
    if (!trainingPlan) {
      showApiError('No training plan available', 'Cannot change workout type');
      return;
    }

    try {
      logger.info('Changing workout type', { workoutIndex, newWorkoutTypeId });
      const response = await apiClient.applyWorkoutChange(workoutIndex, newWorkoutTypeId);

      if (response.success && response.data) {
        const { trainingPlan: updatedPlan, preview } = response.data;

        // Update UI directly - backend already persisted the changes
        setTrainingPlan(updatedPlan);

        // Update the selected workout in the modal with the updated data from preview
        // The preview.changedWorkout.after has the exact updated workout data
        if (selectedWorkout && selectedWorkout.index === workoutIndex) {
          setSelectedWorkout(preview.changedWorkout.after);
        }

        // Update localStorage cache
        localStorage.setItem('trainingPlan', JSON.stringify(updatedPlan));

        // Show success message with details about what changed
        let message = `Changed ${preview.changedWorkout.before.name} to ${preview.changedWorkout.after.name}`;
        if (preview.affectedWorkouts.length > 0) {
          message += ` (${preview.affectedWorkouts.length} adjacent workout${preview.affectedWorkouts.length > 1 ? 's' : ''} adjusted)`;
        }
        showSuccess(message);

        logger.info('Workout type changed successfully', {
          from: preview.changedWorkout.before.name,
          to: preview.changedWorkout.after.name,
          affected: preview.affectedWorkouts.length,
        });
      } else {
        showApiError(response, 'Failed to change workout type');
      }
    } catch (error) {
      logger.error('Workout type change error', error as Error);
      throw error; // Re-throw to let WorkoutModal handle the error display
    }
  };

  // Dashboard now works without Intervals.icu - it's optional
  // If not connected to Intervals.icu, we'll just hide health data widgets

  return (
    <Container maxWidth={false}>
      <Box py={{ xs: 1, sm: 2, xl: 1 }} px={{ xs: 0, sm: 2, xl: 1 }}>
        <Grid container spacing={{ xs: 2, xl: 1 }}>
          {/* Left Sidebar - Only on XL screens */}
          <Grid size={{ xs: 0, xl: 2 }} sx={{ display: { xs: 'none', xl: 'block' } }}>
            <Box position="sticky" top={16}>
              {/* Current Fitness Status */}
              {authState.healthData && (
                <Box mb={2}>
                  <Typography variant="h6" gutterBottom sx={{ px: 2 }}>
                    Current Fitness
                  </Typography>
                  <Grid container spacing={1}>
                    {/* Run km - only show if > 0 */}
                    {authState.healthData?.weekly_run_km > 0 && (
                      <Grid size={12}>
                        <Card>
                          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Box
                              display="flex"
                              flexDirection="column"
                              alignItems="center"
                              textAlign="center"
                            >
                              <DirectionsRun color="primary" sx={{ mb: 1 }} />
                              <Typography variant="h6" fontSize="1rem">
                                {`${Math.round(authState.healthData.weekly_run_km)} km`}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Run last week
                              </Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    )}
                    {/* Bike km - only show if > 0 */}
                    {authState.healthData?.weekly_bike_km > 0 && (
                      <Grid size={12}>
                        <Card>
                          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Box
                              display="flex"
                              flexDirection="column"
                              alignItems="center"
                              textAlign="center"
                            >
                              <DirectionsBike color="secondary" sx={{ mb: 1 }} />
                              <Typography variant="h6" fontSize="1rem">
                                {`${Math.round(authState.healthData.weekly_bike_km)} km`}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Bike last week
                              </Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    )}
                    <Grid size={12}>
                      <Card
                        sx={{
                          border: 2,
                          borderColor:
                            authState.healthData?.atl &&
                            authState.healthData?.ctl &&
                            authState.healthData.atl > authState.healthData.ctl
                              ? 'success.main'
                              : 'primary.main',
                          cursor: 'pointer',
                          '&:hover': {
                            boxShadow: 4,
                            transform: 'scale(1.02)',
                            transition: 'all 0.2s ease-in-out',
                          },
                        }}
                        onClick={() => setIsAtlCtlModalOpen(true)}
                      >
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            textAlign="center"
                            position="relative"
                          >
                            <Box display="flex" alignItems="center" gap={0.5} sx={{ mb: 1 }}>
                              <TrendingUp color="success" />
                              <Tooltip title="Sync fitness data from Intervals.icu">
                                <IconButton
                                  size="small"
                                  onClick={e => {
                                    e.stopPropagation();
                                    syncFitnessData(true, false);
                                  }}
                                  disabled={isSyncingFitness}
                                  aria-label="Sync fitness data from Intervals.icu"
                                  sx={{ p: 0.5 }}
                                >
                                  {isSyncingFitness ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <Sync fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                            </Box>
                            <Typography variant="h6" fontSize="1rem">
                              {authState.healthData?.atl && authState.healthData?.ctl
                                ? `${Math.round(authState.healthData.atl)} / ${Math.round(authState.healthData.ctl)} / `
                                : 'N/A'}
                              {authState.healthData?.atl && authState.healthData?.ctl && (
                                <Box
                                  component="span"
                                  sx={{
                                    color: (() => {
                                      const tsb =
                                        authState.healthData.ctl - authState.healthData.atl;
                                      if (tsb > 20) return 'warning.main';
                                      if (tsb > 5) return 'info.main';
                                      if (tsb > -10) return 'text.secondary';
                                      if (tsb > -30) return 'success.main';
                                      return 'error.main';
                                    })(),
                                  }}
                                >
                                  {Math.round(authState.healthData.ctl - authState.healthData.atl)}
                                </Box>
                              )}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ATL / CTL / TSB (Click for details)
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid size={12}>
                      <Card>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            textAlign="center"
                          >
                            <MonitorHeart color="error" sx={{ mb: 1 }} />
                            <Typography variant="h6" fontSize="1rem">
                              {authState.healthData?.hrv_score
                                ? Math.round(authState.healthData.hrv_score)
                                : 'N/A'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              HRV Score
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Training Actions */}
              {trainingActionState && (
                <Box mb={2}>
                  <Typography variant="h6" gutterBottom sx={{ px: 2 }}>
                    Training Actions
                  </Typography>
                  <TrainingActions
                    isSwapMode={trainingActionState.isSwapMode}
                    isDeloadWeek={trainingActionState.isDeloadWeek}
                    onSwapModeToggle={trainingActionState.onSwapModeToggle}
                    onDeloadWeekClick={trainingActionState.onDeloadWeekClick}
                    onAICoachChangesApplied={() => {
                      // AI Coach changes are applied via event dispatch
                      // The training-plan-updated event will update the state
                      logger.info('AI Coach changes applied callback triggered');
                    }}
                  />
                </Box>
              )}

              {/* Workout Avatar Widget - What to Wear */}
              <Box mb={2}>
                <WorkoutAvatarWidget
                  weatherData={sharedWeather}
                  clothingData={sharedClothing}
                  isLoadingData={isLoadingWeather}
                />
              </Box>

              {/* Health Status Widget */}
              <Box mb={2}>
                <HealthStatusWidget
                  initialStatus={healthStatus}
                  initialHistory={healthStatusHistory}
                  onStatusChange={async (shouldSync, newStatus) => {
                    if (newStatus) {
                      setHealthStatus(newStatus);
                    }
                    if (shouldSync) {
                      // Backend has already applied recovery adjustments and saved to MongoDB
                      // Reload page to fetch the adjusted plan (simplest way to avoid race conditions)
                      logger.info('Reloading page to fetch adjusted plan from backend...');
                      window.location.reload();
                    }
                  }}
                />
              </Box>
            </Box>
          </Grid>

          {/* Main Content Area */}
          <Grid size={{ xs: 12, xl: 10 }}>
            {/* Training Plan Section - Top Priority */}
            <Box mb={{ xs: 1, sm: 2, xl: 0 }}>
              <TrainingCalendar
                trainingPlan={trainingPlan}
                isLoading={isGeneratingPlan || isDeletingWorkout}
                onWorkoutClick={handleWorkoutClick}
                onTrainingPlanUpdate={handleTrainingPlanUpdate}
                onActionStateChange={setTrainingActionState}
                healthStatus={healthStatus}
              />
            </Box>
          </Grid>
        </Grid>

        {/* Fitness Status - Only show on non-XL screens (on XL it's in the sidebar) */}
        <Grid container spacing={2} sx={{ display: { xs: 'flex', xl: 'none' } }}>
          <Grid size={12}>
            {/* What to Wear Widget - Mobile View */}
            <Box mb={2}>
              <WorkoutAvatarWidget
                weatherData={sharedWeather}
                clothingData={sharedClothing}
                isLoadingData={isLoadingWeather}
              />
            </Box>

            {/* Health Status Widget */}
            <Box mb={2}>
              <HealthStatusWidget
                initialStatus={healthStatus}
                initialHistory={healthStatusHistory}
                onStatusChange={async (shouldSync, newStatus) => {
                  if (newStatus) {
                    setHealthStatus(newStatus);
                  }
                  if (shouldSync) {
                    // Backend has already applied recovery adjustments and saved to MongoDB
                    // Reload page to fetch the adjusted plan (simplest way to avoid race conditions)
                    logger.info('Reloading page to fetch adjusted plan from backend');
                    window.location.reload();
                  }
                }}
              />
            </Box>

            {/* Current Fitness Status */}
            {authState.healthData && (
              <Box>
                <Grid container spacing={1}>
                  {/* Run km - only show if > 0 */}
                  {authState.healthData?.weekly_run_km > 0 && (
                    <Grid size={{ xs: 6, sm: 4 }}>
                      <Card>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            textAlign="center"
                          >
                            <DirectionsRun color="primary" sx={{ mb: 1 }} />
                            <Typography variant="h6" fontSize="1rem">
                              {`${Math.round(authState.healthData.weekly_run_km)} km`}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Run last week
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}
                  {/* Bike km - only show if > 0 */}
                  {authState.healthData?.weekly_bike_km > 0 && (
                    <Grid size={{ xs: 6, sm: 4 }}>
                      <Card>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            textAlign="center"
                          >
                            <DirectionsBike color="secondary" sx={{ mb: 1 }} />
                            <Typography variant="h6" fontSize="1rem">
                              {`${Math.round(authState.healthData.weekly_bike_km)} km`}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Bike last week
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}
                  <Grid size={{ xs: 6, sm: 4 }}>
                    <Card
                      sx={{
                        border: 2,
                        borderColor:
                          authState.healthData?.atl &&
                          authState.healthData?.ctl &&
                          authState.healthData.atl > authState.healthData.ctl
                            ? 'success.main'
                            : 'primary.main',
                        cursor: 'pointer',
                        '&:hover': {
                          boxShadow: 4,
                          transform: 'scale(1.02)',
                          transition: 'all 0.2s ease-in-out',
                        },
                      }}
                      onClick={() => setIsAtlCtlModalOpen(true)}
                    >
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                          textAlign="center"
                        >
                          <Box display="flex" alignItems="center" gap={0.5} sx={{ mb: 1 }}>
                            <TrendingUp color="success" />
                            <Tooltip title="Sync fitness data">
                              <IconButton
                                size="small"
                                onClick={e => {
                                  e.stopPropagation();
                                  syncFitnessData(true, false);
                                }}
                                disabled={isSyncingFitness}
                                aria-label="Sync fitness data from Intervals.icu"
                                sx={{ p: 0.25 }}
                              >
                                {isSyncingFitness ? (
                                  <CircularProgress size={14} />
                                ) : (
                                  <Sync sx={{ fontSize: 14 }} />
                                )}
                              </IconButton>
                            </Tooltip>
                          </Box>
                          <Typography variant="h6" fontSize="1rem">
                            {authState.healthData?.atl && authState.healthData?.ctl
                              ? `${Math.round(authState.healthData.atl)} / ${Math.round(authState.healthData.ctl)} / `
                              : 'N/A'}
                            {authState.healthData?.atl && authState.healthData?.ctl && (
                              <Box
                                component="span"
                                sx={{
                                  color: (() => {
                                    const tsb = authState.healthData.ctl - authState.healthData.atl;
                                    if (tsb > 20) return 'warning.main';
                                    if (tsb > 5) return 'info.main';
                                    if (tsb > -10) return 'text.secondary';
                                    if (tsb > -30) return 'success.main';
                                    return 'error.main';
                                  })(),
                                }}
                              >
                                {Math.round(authState.healthData.ctl - authState.healthData.atl)}
                              </Box>
                            )}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ATL / CTL / TSB
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 4 }}>
                    <Card>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                          textAlign="center"
                        >
                          <MonitorHeart color="error" sx={{ mb: 1 }} />
                          <Typography variant="h6" fontSize="1rem">
                            {authState.healthData?.hrv_score
                              ? Math.round(authState.healthData.hrv_score)
                              : 'N/A'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            HRV Score
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Grid>
        </Grid>

        {/* Workout Detail Modal */}
        <WorkoutModal
          key={
            selectedWorkout
              ? `${selectedWorkout.index}-${selectedWorkout.name}-${selectedWorkout.total_distance}-${selectedWorkout.TSS}`
              : 'no-workout'
          }
          workout={selectedWorkout}
          isOpen={isWorkoutModalOpen}
          onClose={handleCloseWorkoutModal}
          onRemove={handleWorkoutRemove}
          isDeleting={isDeletingWorkout}
          onUpdateDistance={handleLongRunDistanceUpdate}
          onChangeWorkoutType={handleWorkoutTypeChange}
          workoutTypes={workoutTypes}
          userProfile={createUserProfile() || undefined}
          trainingPlan={trainingPlan || undefined}
        />

        {/* ATL-CTL Fitness Trend Modal */}
        <AtlCtlModal
          isOpen={isAtlCtlModalOpen}
          onClose={() => setIsAtlCtlModalOpen(false)}
          userId="default_user"
        />

        {/* No Training Plan Dialog */}
        <NoPlanDialog
          open={showNoPlanDialog}
          onClose={handleCloseNoPlanDialog}
          onRunOnboarding={handleRunOnboarding}
          onGeneratePlan={handleGeneratePlanFromDialog}
          isGenerating={isGeneratingPlan}
        />
      </Box>

      {/* Bottom Toolbar */}
      <BottomToolbar />
    </Container>
  );
};

export default Dashboard;
